/**
 * Invite Input Form Component
 *
 * Displays:
 * - Email input field
 * - Cancel button
 * - Send button with loading state
 *
 * Presentational component: No business logic, only displays data
 */
import React from 'react';
import { View, Text, Pressable, TextInput, ActivityIndicator } from 'react-native';
import type { TFunction } from '@dramadetector/shared';
import { useTranslation } from '@dramadetector/shared';

interface InviteInputFormProps {
  inviteEmail: string;
  onChangeEmail: (email: string) => void;
  onCancel: () => void;
  onSend: () => void;
  isInviting: boolean;
  t: TFunction<"subscription">;
}

export function InviteInputForm({
  inviteEmail,
  onChangeEmail,
  onCancel,
  onSend,
  isInviting,
  t,
}: InviteInputFormProps) {
  const { t: tCommon } = useTranslation('common');
  return (
    <View className="bg-white p-6 shadow-sm mb-4">
      <Text className="text-lg font-semibold text-gray-900 mb-3">
        {t('family.inviteByEmail')}
      </Text>
      <TextInput
        className="bg-gray-50 rounded-xl px-4 py-3 mb-3 text-gray-900"
        placeholder={t('family.emailPlaceholder')}
        value={inviteEmail}
        onChangeText={onChangeEmail}
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
      />
      <View className="flex-row gap-3">
        <Pressable
          onPress={onCancel}
          className="flex-1 bg-gray-200 rounded-xl py-3 px-4 active:opacity-80"
        >
          <Text className="text-gray-700 font-semibold text-center">
            {tCommon('cancel')}
          </Text>
        </Pressable>
        <Pressable
          testID="send-invitation"
          onPress={onSend}
          disabled={isInviting}
          className="flex-1 bg-teal-600 rounded-xl py-3 px-4 active:opacity-80"
        >
          {isInviting ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <Text className="text-white font-semibold text-center">
              {tCommon('send')}
            </Text>
          )}
        </Pressable>
      </View>
    </View>
  );
}
